import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';

/**
 * Morphing Button Component
 * Transforms between states: idle → loading → success
 */
const MorphingButton = ({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  className = '',
  showSuccessState = true,
  successDuration = 1500
}) => {
  const [state, setState] = useState('idle'); // idle, loading, success

  const handleClick = async () => {
    if (disabled || state !== 'idle') return;

    setState('loading');

    try {
      await onClick();

      if (showSuccessState) {
        setState('success');
        setTimeout(() => {
          setState('idle');
        }, successDuration);
      } else {
        setState('idle');
      }
    } catch (error) {
      setState('idle');
    }
  };

  const variants = {
    primary: {
      idle: 'bg-blue-600 hover:bg-blue-700',
      loading: 'bg-blue-500',
      success: 'bg-green-500'
    },
    secondary: {
      idle: 'bg-white hover:bg-gray-50 border-2 border-gray-200',
      loading: 'bg-gray-100 border-2 border-gray-300',
      success: 'bg-green-50 border-2 border-green-500'
    }
  };

  const textColors = {
    primary: 'text-white',
    secondary: state === 'success' ? 'text-green-700' : 'text-gray-700'
  };

  return (
    <motion.button
      onClick={handleClick}
      disabled={disabled || state !== 'idle'}
      className={`
        relative overflow-hidden
        px-8 py-3.5 rounded-xl
        font-semibold text-[15px]
        transition-all duration-300
        ${variants[variant][state]}
        ${textColors[variant]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      whileHover={state === 'idle' && !disabled ? { scale: 1.02, y: -2 } : {}}
      whileTap={state === 'idle' && !disabled ? { scale: 0.98 } : {}}
      animate={{
        boxShadow: state === 'success'
          ? '0 8px 24px rgba(34, 197, 94, 0.4)'
          : state === 'loading'
          ? '0 4px 12px rgba(59, 130, 246, 0.3)'
          : '0 4px 12px rgba(59, 130, 246, 0.3)'
      }}
    >
      <AnimatePresence mode="wait">
        {state === 'idle' && (
          <motion.div
            key="idle"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center gap-2"
          >
            {children}
          </motion.div>
        )}

        {state === 'loading' && (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center gap-2"
          >
            <Loader2 className="animate-spin" size={18} />
            <span>Processing...</span>
          </motion.div>
        )}

        {state === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{
              duration: 0.3,
              type: 'spring',
              stiffness: 300,
              damping: 20
            }}
            className="flex items-center justify-center gap-2"
          >
            <motion.div
              initial={{ rotate: -180 }}
              animate={{ rotate: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Check size={20} strokeWidth={3} />
            </motion.div>
            <span>Success!</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success ripple effect */}
      {state === 'success' && (
        <motion.div
          className="absolute inset-0 bg-green-400 rounded-xl"
          initial={{ scale: 0, opacity: 0.5 }}
          animate={{ scale: 2, opacity: 0 }}
          transition={{ duration: 0.6 }}
        />
      )}
    </motion.button>
  );
};

export default MorphingButton;
